import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { DraftStateSlice } from '@/features/jobs/drafting/DraftJobViewModel'
import type {
  StepAccessor,
  StepFactory,
} from '@/features/jobs/drafting/OutlineViewModel'
import { computed, observable, runInAction } from 'mobx'
import type { DraftJob } from '@/features/jobs/drafting/DraftJob'
import { task } from 'mobx-task'
import { toStream } from '@/utils/mobx-util'
import { find } from 'rxjs/operators'
import {
  firstValueFrom,
  merge,
  type Observable,
  of,
  Subject,
  switchMap,
} from 'rxjs'
import {
  filterNonNil,
  fromAbortSignal,
  shareLatest,
  withAbortSignal,
} from '@/utils/rx-util'
import type { CheckoutBillingViewModelFactory } from './CheckoutBillingViewModel'
import type { Quote } from '@/features/jobs/drafting/Quote'
import type { DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import type { SnackbarFactory } from '@/features/shell'
import type { EnsureTermsAcceptanceFn } from '@/features/account'
import { isAbortError } from '@/utils/abort-util'

/**
 * Key used for snackbar deduplication.
 */
const SUBMIT_SNACKBAR_KEY = Symbol('Draft Submit Snackbar')

/**
 * View-model handling the checkout experience for a draft job.
 */
export class CheckoutViewModel implements ActivationLifecycle, DraftStateSlice {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _submitted$ = new Subject<void>()

  /**
   * The step as represented in the outline.
   */
  readonly step

  /**
   * Billing view model.
   */
  readonly billingVm

  /**
   * Emits when the job has been submitted.
   */
  readonly submitted$ = this._submitted$.asObservable()

  /**
   * Whether the checkout step is the active step.
   */
  readonly isCheckoutActive$

  /**
   * The current quote, if any.
   */
  @observable accessor quote: Quote | null = null

  constructor(
    public readonly job: DraftJob,
    private readonly _draftStore: DraftJobStore,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _ensureTermsAcceptance: EnsureTermsAcceptanceFn,
    reloadSummary$: Observable<unknown>,
    createCheckoutBillingViewModel: CheckoutBillingViewModelFactory,
    createStep: StepFactory,
    private readonly _isReadyForCheckout: () => boolean,
  ) {
    this.step = createStep({
      stepId: 'checkout',
      isCompleted: () => false,
      isShortCircuited: () => false,
      outlineItemDisplay: () => ({ title: 'Checkout' }),
    })

    const quote$ = toStream(() => this.quote, true).pipe(filterNonNil())
    this.billingVm = createCheckoutBillingViewModel(quote$, () => this.submit())

    this._lifecycle.manage(this.billingVm)

    const isActive$ = toStream(() => this.step.isActive, true).pipe(
      shareLatest(),
    )
    this.isCheckoutActive$ = isActive$
    const becomeActive$ = isActive$.pipe(find(Boolean))
    const mergedReload$ = merge(reloadSummary$, this.billingVm.couponChanged$)
    const fetchQuote$ = becomeActive$.pipe(
      switchMap(() =>
        merge(
          // Fetch immediately on becoming active for the first time.
          of(true),
          // When we're asked to reload:
          mergedReload$.pipe(
            // Queue a reload for when we become active again
            switchMap(() => becomeActive$),
          ),
        ),
      ),
      switchMap(() => withAbortSignal((signal) => this.fetchQuote(signal))),
    )

    this._lifecycle.manage(fetchQuote$)

    this._lifecycle.manage(
      // Fetch billing info the first time the checkout step
      // becomes active.
      becomeActive$.pipe(
        switchMap(() =>
          withAbortSignal((signal) => this.billingVm.fetchBillingInfo(signal)),
        ),
      ),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.submit.reset()
  }

  /** @inheritDoc */
  @computed get stepAccessors(): StepAccessor[] {
    if (!this._isReadyForCheckout()) {
      return []
    }

    return [this]
  }

  /**
   * Whether the quote is enabled on the Legend.
   */
  @computed get isQuoteEnabled() {
    return this.job.legend.isQuoteEnabled
  }

  /**
   * Whether we're currently fetching the initial data.
   */
  @computed get isFetchingInitial() {
    const isInitialSummaryFetch = this.fetchQuote.pending && !this.quote
    return this.billingVm.isFetchingBillingInfo || isInitialSummaryFetch
  }

  /**
   * Whether we're currently fetching anything.
   */
  @computed get isFetching() {
    return this.isFetchingInitial || this.fetchQuote.pending
  }

  /**
   * Whether the Submit button is disabled.
   */
  @computed get isSubmitEnabled() {
    if (this.isFetching) {
      return false
    }

    return this.billingVm.isBillingSubmitEnabled
  }

  /** @inheritDoc */
  async prepare(_signal?: AbortSignal): Promise<unknown> {
    /* Nothing to fetch that needs to block the UI */
    return Promise.resolve()
  }

  /**
   * Fetches the quote.
   */
  fetchQuote = task(async (signal?: AbortSignal) => {
    if (!this.isQuoteEnabled) {
      return
    }

    const quote = await this._draftStore.fetchQuote(this.job.id, signal)
    signal?.throwIfAborted()
    runInAction(() => {
      // Don't show quote if it's practically empty.
      this.quote =
        quote.items.length === 0 && quote.price === null ? null : quote
    })
  })

  /**
   * Submits the job.
   */
  submit = task.resolved(async () => {
    const signal = this._lifecycle.signal
    const signal$ = fromAbortSignal(signal)
    const submissionSnackbar = this._submissionSnackbar(signal$)

    try {
      // If we're asking for a new billing method, save it first, then
      // submit the job.
      if (this.billingVm.isBillingEnabled) {
        const billingSuccess = await this.billingVm.trySaveNewBillingMethod()
        if (!billingSuccess) {
          // Creating the method failed, so abort submission.
          submissionSnackbar.billingError()
          return
        }
      }

      const termsOutcome = await this._ensureTermsAcceptance(
        this.job.workspace.id,
        signal,
      )

      if (termsOutcome !== 'Accepted') {
        return
      }

      submissionSnackbar.submitting()
      const submissionId = await this._draftStore.submit({
        id: this.job.id,
        billingMethodId: this.billingVm.selectedBillingMethodId,
      })
      submissionSnackbar.processing()
      await this._draftStore.waitForSubmissionOutcome({ submissionId, signal })

      // The job was submitted - the view will observe this and
      // redirect to job details.
      this._submitted$.next()
      submissionSnackbar.dismiss()

      // This makes sure the view doesn't unblock while we wait for
      // the redirect.
      await firstValueFrom(signal$, { defaultValue: null })
    } catch (error: unknown) {
      if (isAbortError(error)) {
        return
      }

      submissionSnackbar.error(error)
    }
  })

  /**
   * Wrapper for a snackbar for the submission process.
   *
   * @param dismiss$
   * @private
   */
  private _submissionSnackbar(dismiss$: Observable<unknown>) {
    const snackbar = this._newSnackbar((s) =>
      s
        .withKey(SUBMIT_SNACKBAR_KEY)
        .type('pending')
        .saying('Your request is being submitted')
        .until(dismiss$),
    )

    return {
      billingError() {
        snackbar.update((s) =>
          s
            .type('error')
            .saying(
              'Error',
              'Please make sure your billing information is correct.',
            )
            .show()
            .autoDismiss(),
        )
      },
      submitting() {
        snackbar.show()
      },
      processing() {
        snackbar.saying(
          `Your request is being processed`,
          'This may take a few seconds, please wait.',
        )
      },
      error(error: unknown) {
        snackbar.fromError(error)
      },
      dismiss() {
        snackbar.dismiss()
      },
    }
  }
}

/**
 * @see CheckoutViewModelFactory
 */
export type CheckoutViewModelFactory = ReturnType<
  typeof createCheckoutViewModelFactory
>

/**
 * @see CheckoutViewModelFactory
 */
export interface CheckoutViewModelOptions {
  isReadyForCheckout: () => boolean
  createStep: StepFactory
  reloadSummary$: Observable<unknown>
}

/**
 * Factory for creating a {@link CheckoutViewModel}.
 */
export function createCheckoutViewModelFactory(
  job: DraftJob,
  draftStore: DraftJobStore,
  newSnackbar: SnackbarFactory,
  ensureTermsAcceptance: EnsureTermsAcceptanceFn,
  createCheckoutBillingViewModel: CheckoutBillingViewModelFactory,
) {
  return ({
    isReadyForCheckout,
    reloadSummary$,
    createStep,
  }: CheckoutViewModelOptions) =>
    new CheckoutViewModel(
      job,
      draftStore,
      newSnackbar,
      ensureTermsAcceptance,
      reloadSummary$,
      createCheckoutBillingViewModel,
      createStep,
      isReadyForCheckout,
    )
}
